/**
 * @fileoverview One-shot client-side recovery for transient browser errors.
 * @description Attempts a single full-page reload when a recoverable error
 * (chunk load failure, DOM reconciliation after external mutation) is detected, using sessionStorage to prevent reload loops.
 *
 * @module lib/client-error-recovery
 */

import {
  classifyClientError,
  type ClientErrorCategory,
  type ClientErrorClassification,
} from '~/lib/client-error-classification'

/** sessionStorage key prefix — scoped per deployment when available. */
const RELOAD_GUARD_PREFIX = 'adsights:client-error-reload:'

/**
 * In-memory fallback when `sessionStorage` is unavailable or throws (private
 * browsing quota). Without this, every recoverable error would hard-reload in a
 * loop because the guard write silently fails.
 */
const inMemoryReloadGuards = new Set<string>()

/** Clears in-memory reload guards (unit tests only). */
export function resetInMemoryReloadGuardsForTests(): void {
  inMemoryReloadGuards.clear()
}

/**
 * Next.js reads `data-dpl-id` once at bootstrap and caches it on
 * `globalThis.NEXT_DEPLOYMENT_ID` before deleting the DOM attribute.
 */
function readNextDeploymentIdGlobal(): string | undefined {
  if (typeof globalThis === 'undefined') {
    return undefined
  }
  const fromGlobal = (globalThis as { NEXT_DEPLOYMENT_ID?: unknown })
    .NEXT_DEPLOYMENT_ID
  return typeof fromGlobal === 'string' && fromGlobal.length > 0
    ? fromGlobal
    : undefined
}

/**
 * Reads the active Next.js deployment id. Prefer Next's cached client global
 * (survives after hydration removes `data-dpl-id`), then the DOM attribute
 * during the brief window before Next strips it.
 *
 * @returns Deployment id when present.
 */
export function readClientDeploymentId(): string | undefined {
  const fromGlobal = readNextDeploymentIdGlobal()
  if (fromGlobal) {
    return fromGlobal
  }
  if (typeof document === 'undefined') {
    return undefined
  }
  const fromDataset = document.documentElement.dataset.dplId
  if (typeof fromDataset === 'string' && fromDataset.length > 0) {
    return fromDataset
  }
  return undefined
}

/**
 * Storage key for the reload guard. Scoped per deployment AND error category so
 * a chunk_load reload does not block a later dom_reconciliation reload on the
 * same page (observed in production on email-thread-mockup-generator).
 *
 * @param deploymentId - Active deployment id, if known.
 * @param category - Classified error category.
 * @returns sessionStorage key for the reload guard.
 */
export function reloadGuardKey(
  deploymentId?: string,
  category?: ClientErrorCategory,
): string {
  return `${RELOAD_GUARD_PREFIX}${deploymentId ?? 'unknown'}:${category ?? 'unknown'}`
}

/**
 * Whether a one-shot reload has already been attempted for this deployment in
 * the current tab session.
 *
 * @param deploymentId - Active deployment id, if known.
 * @param category - Classified error category.
 * @returns `true` when reload was already attempted.
 */
export function hasAttemptedClientErrorReload(
  deploymentId?: string,
  category?: ClientErrorCategory,
): boolean {
  const key = reloadGuardKey(deploymentId, category)
  if (inMemoryReloadGuards.has(key)) {
    return true
  }
  try {
    if (typeof sessionStorage === 'undefined') {
      return false
    }
    return sessionStorage.getItem(key) === '1'
  } catch {
    // Web Storage disabled — treat as no prior reload attempt in persistent storage.
    return false
  }
}

/**
 * Records that a reload was attempted so we do not loop.
 *
 * @param deploymentId - Active deployment id, if known.
 * @param category - Classified error category.
 * @returns `true` when the guard was persisted to `sessionStorage` (survives reload).
 */
export function markClientErrorReloadAttempted(
  deploymentId?: string,
  category?: ClientErrorCategory,
): boolean {
  const key = reloadGuardKey(deploymentId, category)
  inMemoryReloadGuards.add(key)
  try {
    if (typeof sessionStorage === 'undefined') {
      return false
    }
    sessionStorage.setItem(key, '1')
    return true
  } catch {
    // In-memory guard only covers same-document duplicates; cannot survive reload.
    return false
  }
}

export interface ClientErrorRecoveryResult {
  /** Whether a reload was triggered. */
  reloaded: boolean
  /** Classification that drove the decision. */
  classification: ClientErrorClassification
  /** Deployment id used for the reload guard, when known. */
  deploymentId?: string
  /**
   * True when the error is recoverable but a reload was already attempted for
   * this deployment+category in the tab — log at `error` so repeat faults alert.
   */
  recoveryBlocked?: boolean
  /**
   * True when auto-recovery was skipped because the reload guard could not be
   * written to `sessionStorage` (private browsing / quota) — reloading would loop.
   */
  recoveryStorageUnavailable?: boolean
}

/**
 * Performs a hard navigation reload. Extracted so tests can stub without
 * fighting jsdom's read-only `window.location`.
 */
export function triggerHardReload(): void {
  window.location.reload()
}

/**
 * Whether a scoped error boundary should auto-reload for this fault.
 * Chunk load failures benefit from one-shot reload (deploy skew); DOM
 * reconciliation faults show the boundary fallback with a manual reload button.
 *
 * @param classification - Classified client error.
 * @returns `true` when the boundary should trigger auto-recovery.
 */
export function shouldAutoRecoverWithBoundaryUi(
  classification: ClientErrorClassification,
): boolean {
  return (
    classification.recoverable &&
    classification.errorCategory === 'chunk_load'
  )
}

/**
 * Recovery options for client error auto-reload.
 */
export interface ClientErrorRecoveryOptions {
  /** Optional reload override (tests only). */
  reload?: () => void
  /**
   * When `false`, classify only — no reload guard write and no navigation.
   * Error boundaries with their own fallback UI should pass `recover: false`.
   */
  recover?: boolean
}

/**
 * Evaluates whether a recoverable error should trigger a one-shot reload.
 *
 * @param error - The error that surfaced to the global handler or window listener.
 * @param options - Recovery options (`recover` defaults to `true`).
 * @returns Classification and whether reload is eligible (not yet executed).
 */
export function evaluateClientErrorRecovery(
  error: unknown,
  options: ClientErrorRecoveryOptions = {},
): ClientErrorRecoveryResult {
  const normalized =
    error instanceof Error ? error : new Error(String(error))
  const classification = classifyClientError(normalized)
  const recover = options.recover !== false

  if (
    !recover ||
    !classification.recoverable ||
    typeof window === 'undefined'
  ) {
    return { reloaded: false, classification }
  }

  const deploymentId =
    readClientDeploymentId() ?? classification.chunkDeploymentId
  const category = classification.errorCategory

  if (hasAttemptedClientErrorReload(deploymentId, category)) {
    return {
      reloaded: false,
      classification,
      deploymentId,
      recoveryBlocked: true,
    }
  }

  return { reloaded: false, classification, deploymentId }
}

/**
 * For recoverable transient client errors, perform at most one full-page reload
 * per tab session (per deployment). Call before or after logging — logging the
 * first occurrence is still valuable for volume tracking even when we recover.
 *
 * @param error - The error that surfaced to the global handler or window listener.
 * @param options.reload - Optional reload override (tests only).
 * @param options.recover - When `false`, skip reload (log-only path).
 * @returns Whether a reload was triggered and the classification used.
 */
export function maybeRecoverFromClientError(
  error: unknown,
  options: ClientErrorRecoveryOptions = {},
): ClientErrorRecoveryResult {
  const evaluation = evaluateClientErrorRecovery(error, options)

  if (evaluation.recoveryBlocked || !evaluation.classification.recoverable) {
    return evaluation
  }

  const recover = options.recover !== false
  if (!recover || typeof window === 'undefined') {
    return evaluation
  }

  const deploymentId =
    evaluation.deploymentId ??
    readClientDeploymentId() ??
    evaluation.classification.chunkDeploymentId
  const category = evaluation.classification.errorCategory

  if (hasAttemptedClientErrorReload(deploymentId, category)) {
    return {
      reloaded: false,
      classification: evaluation.classification,
      deploymentId,
      recoveryBlocked: true,
    }
  }

  const guardPersisted = markClientErrorReloadAttempted(deploymentId, category)
  if (!guardPersisted) {
    return {
      reloaded: false,
      classification: evaluation.classification,
      deploymentId,
      recoveryStorageUnavailable: true,
    }
  }

  const reload = options.reload ?? triggerHardReload
  reload()
  return {
    reloaded: true,
    classification: evaluation.classification,
    deploymentId,
  }
}